import React, { useCallback, useEffect, useMemo, useState } from 'react';
import type { FreestandingLineKind, Vendor } from '@techgitter/contracts';
import { createStockLine } from '../../services/partOrdersApi';
import { getVendors } from '../../services/vendorService';
import { useProductCatalog } from '../../hooks/useProductCatalog';
import { SidePanel } from '../panel/SidePanel';
import { FormField } from '../panel/FormField';
import { SmartSelect, SearchAhead } from '../dropdown';
import './AddStockLinePanel.css';

const GST_RATE = 0.1;

type GstMode = 'ex' | 'inc';

const KIND_LABEL: Record<FreestandingLineKind, string> = {
  stock: 'Stock',
  internal_use: 'Internal Use',
};

interface Props {
  /** Initial line type. Caller-controlled so PartOrdersPage can launch either flow. */
  initialKind?: FreestandingLineKind;
  onClose: (created: boolean) => void;
}

/**
 * Right-aligned panel for adding a free-standing part-order line. Reuses the
 * shared `SidePanel` shell + `FormField` controls (the former duplicated
 * `.panel__*` chrome has been retired), so it reads identically to the calendar
 * Schedule Appointment / Add Leave, To-Do, and Repair Services panels. Lives in
 * the right column of PartOrdersPage's `.part-orders-layout` grid. The buyer types
 * the part details directly — product URL, code, description, ex-GST cost — and
 * picks the vendor from the master list. A Line type radio selects between resale
 * stock and internal-use parts; both share the same vendor + cost fields and land
 * in the To Order list.
 */
export const AddStockLinePanel: React.FC<Props> = ({ initialKind = 'stock', onClose }) => {
  const [kind, setKind] = useState<FreestandingLineKind>(initialKind);
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [vendorsLoading, setVendorsLoading] = useState(false);
  const [vendorId, setVendorId] = useState('');
  const [productUrl, setProductUrl] = useState('');
  const [productCode, setProductCode] = useState('');
  const [description, setDescription] = useState('');
  const [gstMode, setGstMode] = useState<GstMode>('ex');
  // Cost is stored as-typed; interpretation depends on gstMode and is
  // converted to ex-GST on submit (the canonical persisted form).
  const [cost, setCost] = useState('');
  const [qty, setQty] = useState('1');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isStock = kind === 'stock';

  // Vendor options for the shared SmartSelect.
  const vendorOptions = vendors.map((v) => ({ value: v.vendorId, label: v.name }));

  // Product-code auto-select: search the catalog by SKU or name; selecting a
  // product fills the code and populates the Description with its description.
  const { products } = useProductCatalog();
  const productResults = useMemo(() => {
    const q = productCode.trim().toLowerCase();
    const base = products.filter((p) => !p.disabled);
    const matched = q
      ? base.filter(
          (p) =>
            p.sku.toLowerCase().includes(q) ||
            p.displayName.toLowerCase().includes(q),
        )
      : base;
    return matched
      .slice(0, 50)
      .map((p) => ({ code: p.sku, description: p.displayName, productDescription: p.description }));
  }, [products, productCode]);

  // Keep the line type in sync when the launcher re-opens with a different kind
  // (e.g. user clicks "New internal-use item" while the panel is already open).
  useEffect(() => {
    setKind(initialKind);
  }, [initialKind]);

  useEffect(() => {
    const controller = new AbortController();
    setVendorsLoading(true);
    getVendors(controller.signal)
      .then((data) => setVendors(data.filter((v) => v.isActive)))
      .catch(() => {})
      .finally(() => setVendorsLoading(false));
    return () => controller.abort();
  }, []);

  // Guard close while a submit is in flight so the panel doesn't unmount
  // mid-request (Cancel, Escape, and the × close all route through this).
  const handleClose = useCallback(() => {
    if (!submitting) onClose(false);
  }, [submitting, onClose]);

  // Escape closes the panel. (When the vendor SmartSelect is open it consumes
  // Escape and stops propagation, so only the dropdown closes.)
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [handleClose]);

  const selectGst = (next: GstMode) => {
    if (next === gstMode) return;
    const factor = next === 'inc' ? 1 + GST_RATE : 1 / (1 + GST_RATE);
    if (cost !== '') {
      const num = Number(cost);
      if (Number.isFinite(num)) setCost((num * factor).toFixed(2));
    }
    setGstMode(next);
  };

  const canSubmit =
    !!vendorId &&
    description.trim().length > 0 &&
    Number(qty) > 0 &&
    Number.isFinite(Number(cost)) &&
    !submitting;

  const handleSubmit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const costNum = Number(cost);
      const costExGst = gstMode === 'inc' ? costNum / (1 + GST_RATE) : costNum;
      await createStockLine({
        vendor_id: vendorId,
        sku: productCode.trim(),
        description: description.trim(),
        qty: Math.max(1, Math.floor(Number(qty))),
        expected_unit_cost: Number(costExGst.toFixed(2)),
        product_url: productUrl.trim() || undefined,
        kind,
      });
      onClose(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : `Failed to add ${KIND_LABEL[kind].toLowerCase()} line`);
    } finally {
      setSubmitting(false);
    }
  };

  const lineTypeField = (
    <div className="panel-field-group">
      <span className="panel-field-group__label">Line type</span>
      <div className="radio-option-group">
        <label className={`radio-option ${isStock ? 'radio-option--selected' : ''}`}>
          <input
            type="radio"
            name="lineType"
            value="stock"
            checked={isStock}
            onChange={() => setKind('stock')}
          />
          <span className="radio-option__label">Stock</span>
        </label>
        <label className={`radio-option ${!isStock ? 'radio-option--selected' : ''}`}>
          <input
            type="radio"
            name="lineType"
            value="internal"
            checked={!isStock}
            onChange={() => setKind('internal_use')}
          />
          <span className="radio-option__label">Internal use</span>
        </label>
      </div>
    </div>
  );

  const vendorField = (
    <SmartSelect
      label="Vendor *"
      value={vendorId || null}
      options={vendorOptions}
      onChange={(val) => setVendorId(val ?? '')}
      placeholder={vendorsLoading ? 'Loading…' : 'Select vendor…'}
      disabled={vendorsLoading}
    />
  );

  const descriptionField = (
    <FormField label="Description *">
      <textarea
        rows={2}
        value={description}
        onChange={(e) => setDescription(e.target.value)}
      />
    </FormField>
  );

  const qtyField = (
    <FormField label="Qty">
      <input
        type="text"
        inputMode="numeric"
        value={qty}
        onChange={(e) => setQty(e.target.value)}
        style={{ textAlign: 'right' }}
      />
    </FormField>
  );

  const costField = (
    <FormField label={`Cost (${gstMode === 'inc' ? 'inc' : 'ex'} GST) *`}>
      <input
        type="text"
        inputMode="decimal"
        value={cost}
        onChange={(e) => setCost(e.target.value)}
        placeholder="0.00"
        style={{ textAlign: 'right' }}
      />
    </FormField>
  );

  const gstField = (
    <div className="panel-field-group">
      <span className="panel-field-group__label">GST</span>
      <div className="radio-option-group">
        <label className={`radio-option ${gstMode === 'ex' ? 'radio-option--selected' : ''}`}>
          <input
            type="radio"
            name="gst"
            value="ex"
            checked={gstMode === 'ex'}
            onChange={() => selectGst('ex')}
          />
          <span className="radio-option__label">Ex GST</span>
        </label>
        <label className={`radio-option ${gstMode === 'inc' ? 'radio-option--selected' : ''}`}>
          <input
            type="radio"
            name="gst"
            value="inc"
            checked={gstMode === 'inc'}
            onChange={() => selectGst('inc')}
          />
          <span className="radio-option__label">Inc GST</span>
        </label>
      </div>
    </div>
  );

  return (
    <SidePanel
      title="New Part Order"
      submitLabel={submitting ? 'Adding…' : 'Add to basket'}
      isValid={canSubmit}
      onClose={handleClose}
      onSubmit={handleSubmit}
    >
      {error && <div className="side-panel__alert side-panel__alert--error">{error}</div>}

      {lineTypeField}
      {vendorField}

      <FormField label="Product URL">
        <input
          type="text"
          value={productUrl}
          onChange={(e) => setProductUrl(e.target.value)}
          placeholder="https://…"
        />
      </FormField>

      <SearchAhead
        label="Product code"
        placeholder="Search or type a product code…"
        value={productCode}
        results={productResults}
        onSearch={setProductCode}
        onSelect={(r) => {
          setProductCode(r.code);
          setDescription(r.productDescription);
        }}
      />

      {descriptionField}

      <div className="add-stock-row">
        {qtyField}
        {costField}
      </div>

      {gstField}
    </SidePanel>
  );
};
